<template>
  <div id="natcon-lom-award-create" class="professional-award-form">
    <!-- Professional Overlay Loading -->
    <v-overlay :value="OverlayLoadingFlag" class="professional-overlay">
      <div class="loading-container">
        <v-progress-circular indeterminate size="64" width="4" color="#1976d2"></v-progress-circular>
        <p class="loading-text mt-4">Processing your award application...</p>
      </div>
    </v-overlay>

    <!-- Professional Main Container -->
    <v-card class="main-form-container" elevation="4">
      <!-- Professional Header Section -->
      <v-card-title class="professional-form-header pa-6">
        <div class="header-content">
          <div class="title-section">
            <div class="main-title-wrapper">
              <v-icon size="32" color="#1976d2" class="mr-3">mdi-trophy</v-icon>
              <div class="title-text">
                <h1 class="form-main-title">{{ PageTitle }}</h1>
                <div class="award-badge mt-2">
                  <v-chip color="#ff9800" text-color="white" small>
                    <v-icon left size="14">mdi-star</v-icon>
                    {{ AwardTypeTitleTxt }}
                  </v-chip>
                </div>
              </div>
            </div>
          </div>

          <div class="action-buttons">
            <router-link to="/jac/national/zone-award/list">
              <v-btn color="#ff9800" elevation="2" rounded class="action-btn mr-3 white--text">
                <v-icon left size="18">mdi-arrow-left</v-icon>
                Back
              </v-btn>
            </router-link>

            <v-btn @click.prevent="refreshPageData" color="#2ecc71" elevation="2" rounded
              class="action-btn white--text">
              <v-icon left size="18">mdi-refresh</v-icon>
              Refresh
            </v-btn>
          </div>
        </div>

        <!-- Professional Description Section -->
        <div class="description-section mt-4" v-if="PageDescription">
          <div class="description-wrapper">
            <v-icon size="20" color="#666" class="mr-2">mdi-information-outline</v-icon>
            <p class="form-description">{{ PageDescription }}</p>
          </div>
        </div>
      </v-card-title>

      <!-- Professional Content Container -->
      <v-card-text class="form-main-content pa-6">
        <v-container>
          <!-- Professional Loading Section -->
          <div class="form-loading-section" v-if="LoadingFlag">
            <div class="loading-wrapper">
              <v-progress-circular indeterminate color="#1976d2" size="48" width="4"></v-progress-circular>
              <p class="loading-text mt-4">Loading award criteria...</p>
            </div>
          </div>
          <v-row wrap v-if="ValidateAwardCriteriaFlag">
            <v-col cols="12">
              <!-- success state when member is registered for NatCon -->
              <template v-if="NatconRegistrationFlag">
                <!-- SUCCESS STATE: LOM Group Eligible -->
                <v-card class="natcon-alert-card pa-3 elevation-1 mt-3" style="
                    background: rgba(0, 200, 83, 0.12);
                    border: 1px solid rgba(0, 200, 83, 0.3);
                  ">
                  <v-row align="center" no-gutters>
                    <v-col cols="auto">
                      <v-avatar size="56" class="mr-4" color="green lighten-5">
                        <v-icon color="green darken-2" size="28">mdi-check-circle</v-icon>
                      </v-avatar>
                    </v-col>

                    <v-col>
                      <div class="natcon-alert-title" style="font-weight: 600; color: #1b5e20">
                        National Conference Registration Verified
                      </div>

                      <div class="natcon-alert-msg" style="color: #2e7d32">
                        This LO has successfully
                        <strong>met the eligibility criteria</strong> for the
                        National Conference Registration.

                        <div class="natcon-alert-extra mt-2" style="color: #2e7d32">
                          You may continue with the LO Award nomination process.
                        </div>
                      </div>
                    </v-col>

                    <v-col cols="auto" class="d-none d-sm-flex justify-end">
                      <v-chip outlined small color="green lighten-5" text-color="green darken-2">
                        Eligible
                      </v-chip>
                    </v-col>
                  </v-row>
                </v-card>
              </template>

              <!-- error state when not registered (keeps existing look + extra line) -->
              <template v-else>
                <!-- ERROR STATE: LOM Group NOT Eligible -->
                <v-card class="natcon-alert-card pa-3 elevation-1 mt-3">
                  <v-row align="center" no-gutters>
                    <v-col cols="auto">
                      <v-avatar size="56" class="mr-4 pulse-icon" color="red lighten-5">
                        <v-icon color="red darken-2" size="28">mdi-account-alert</v-icon>
                      </v-avatar>
                    </v-col>

                    <v-col>
                      <div class="natcon-alert-title">
                        National Conference Registration Required
                      </div>

                      <div class="natcon-alert-msg">
                        This LO has
                        <strong>not met the eligibility requirements</strong>
                        for the National Conference Registration. Nomination
                        cannot proceed until all mandatory conditions are
                        completed.

                        <div class="natcon-alert-extra mt-2">
                          This LO must have at least one National Conference
                          Registration to proceed with the nomination process.
                        </div>
                      </div>
                    </v-col>

                    <v-col cols="auto" class="d-none d-sm-flex justify-end">
                      <v-chip outlined small color="red lighten-5" text-color="red darken-2">
                        Not Eligible
                      </v-chip>
                    </v-col>
                  </v-row>
                </v-card>
              </template>
            </v-col>
          </v-row>
          <br />
          <br />
          <jci-india-scoring-table v-if="AwardCategoryId > 0 && AutomaticScoreFlag" :AwardCategoryId="AwardCategoryId"
            :AwardTypeId="AwardTypeId" :LomId="LomId" class="scoring-component"></jci-india-scoring-table>

          <v-form ref="form1" v-model="valid1" lazy-validation v-on:submit.prevent="confirmAlert"
            class="professional-form">
            <div class="form-content-wrapper">
              <v-card class="criteria-card mb-6" elevation="3" v-if="CriteriaAnswers.length > 0">
                <v-card-title class="criteria-header">
                  <v-icon size="24" color="#1976d2" class="mr-3">mdi-clipboard-text</v-icon>
                  <span class="section-title">Judging Criteria</span>
                </v-card-title>

                <v-card-text class="pa-6">
                  <div class="criteria-question-section mb-6" v-for="item in CriteriaAnswers" :key="item.SNo">
                    <div class="criteria-detail-header mb-4" v-if="item.JudgingCriteriaDetailFlag">
                      <div class="criteria-detail-card">
                        <div class="criteria-detail-content">
                          <h3 class="criteria-title">
                            {{ item.JudgingCriteriaDetail }}
                            <small>(score - {{ item.JudgingCriteriaScore }})</small>
                          </h3>
                        </div>
                      </div>
                    </div>

                    <div class="question-content-wrapper">
                      <div class="question-label-section mb-3">
                        <h3 class="question-title">
                          <span class="required-indicator">*</span>
                          {{ item.CriteriaQuestionsText }}
                        </h3>
                      </div>

                      <div class="question-input-section mb-4">
                        <div class="html-editor-wrapper">
                          <vue-editor v-model="item.AnswerText" :editorOptions="editorOptions"
                            @text-change="onTextChange($event, item)" class="professional-editor"></vue-editor>
                          <div class="character-counter" :class="{
                            'error--text':
                              getCharacterCount(item.AnswerText) >
                              item.CriteriaQuestionsCharsCount,
                          }">
                            {{ getCharacterCount(item.AnswerText) }} /
                            {{ item.CriteriaQuestionsCharsCount }} characters
                          </div>
                        </div>
                      </div>

                      <div class="attachments-section mt-5" v-if="AttachmentFlag">
                        <div class="attachments-header">
                          <h3 class="attachments-title">
                            <v-icon size="20" color="#ff9800" class="mr-2">mdi-paperclip</v-icon>
                            Attachments
                            <small>(Max:
                              {{ item.CriteriaQuestionsAttachmentMax }})</small>
                          </h3>

                          <v-btn color="primary" fab small elevation="2" class="add-attachment-btn" v-if="
                            item.CriteriaQuestionsAttachmentMax >
                            item.AllProject.length
                          " @click="addNewRow(item)">
                            <v-icon>mdi-plus</v-icon>
                          </v-btn>
                        </div>

                        <div class="attachments-grid mt-4" v-if="item.AllProject.length > 0">
                          <v-row>
                            <v-col cols="12" sm="6" md="4" lg="3" v-for="(data, index) in item.AllProject" :key="index"
                              v-if="data.AnswerEventId != ''">
                              <v-card class="attachment-card" elevation="2">
                                <div class="attachment-actions">
                                  <v-btn icon color="error" small @click="deleteRow(item.SNo, index)"
                                    class="delete-btn"><v-icon>mdi-delete</v-icon></v-btn>
                                </div>
                                <v-card-text>
                                  <div v-if="
                                    data.AnswerEventPhoto &&
                                    data.AnswerEventPhoto.length
                                  " class="event-images text-center mb-3">
                                    <viewer rebuild :images="data.AnswerEventPhoto">
                                      <div class="images-grid">
                                        <img class="event-image" width="100" height="100" :src="src" :key="src"
                                          v-for="src in data.AnswerEventPhoto" />
                                      </div>
                                    </viewer>
                                  </div>

                                  <div class="event-details">
                                    <div class="detail-row mb-2">
                                      <v-icon size="16" color="#666" class="mr-1">mdi-calendar</v-icon><span
                                        class="detail-label">Event Date:</span><span class="detail-value">{{
                                          data.AnswerEventDate
                                        }}</span>
                                    </div>
                                    <div class="detail-row mb-2">
                                      <v-icon size="16" color="#666" class="mr-1">mdi-domain</v-icon><span
                                        class="detail-label">Area:</span><span class="detail-value">{{
                                          data.AnswerPortfolioName
                                        }}</span>
                                    </div>
                                    <div class="detail-row">
                                      <v-icon size="16" color="#666" class="mr-1">mdi-tag</v-icon><span
                                        class="detail-label">Event
                                        Name:</span><span class="detail-value">{{
                                          data.AnswerEventName
                                        }}</span>
                                    </div>
                                  </div>
                                </v-card-text>
                              </v-card>
                            </v-col>
                          </v-row>
                        </div>

                        <div class="no-attachments-state mt-4" v-else>
                          <v-alert type="info" text color="grey lighten-1" outlined dense>No attachments added yet.
                            Click the +
                            button to add
                            an attachment.</v-alert>
                        </div>
                      </div>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <!-- Empty State for No Criteria -->
              <v-card v-if="CriteriaAnswers.length === 0" class="empty-state-card" elevation="3">
                <v-card-text class="text-center pa-6">
                  <v-icon size="64" color="#ccc">mdi-clipboard-outline</v-icon>
                  <h3 class="empty-state-title mt-4">No Criteria Available</h3>
                  <p class="empty-state-text">
                    There are no judging criteria available for this award at
                    this time.
                  </p>
                </v-card-text>
              </v-card>

              <!-- Additional Attachments Section -->
              <template v-if="CriteriaAnswers.length > 0">
                <v-card class="attachments-additional-card mb-6 elevation-3" style="margin-top: 2rem">
                  <v-card-title class="attachments-additional-header">
                    <v-icon size="24" color="#ff9800" class="mr-3">mdi-image-multiple</v-icon>
                    <span class="section-title">Additional Images</span>
                    <v-spacer></v-spacer>
                    <v-btn v-if="AllProjectMax > AllProject.length" icon small color="#1976d2"
                      class="add-attachment-action-btn" @click="addAttachmentNewRow()"
                      v-tooltip="'Add Supporting Image'">
                      <v-icon>mdi-plus-circle</v-icon>
                    </v-btn>
                  </v-card-title>

                  <v-card-text class="pa-6">
                    <!-- Info Alert -->
                    <v-alert type="info" text outlined class="info-alert mb-6">
                      <div class="d-flex align-center">
                        <v-icon size="20" color="#1976d2" class="mr-3">mdi-information-variant</v-icon>
                        <div>
                          <strong>Image Requirements:</strong>
                          <div class="text-sm mt-1">
                            • Supported formats: JPG, PNG, JPEG<br />
                            • Maximum file size: 2MB per image<br />
                            • Maximum attachments: {{ AllProjectMax }}
                          </div>
                        </div>
                      </div>
                    </v-alert>

                    <!-- Empty State -->
                    <div v-if="AllProject.length === 0" class="empty-state-container">
                      <v-img
                        src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 150'%3E%3Ccircle cx='100' cy='75' r='40' fill='%23e3f2fd'/%3E%3Cpath d='M85 65h30v20H85z' fill='%231976d2'/%3E%3Cpath d='M95 75h10v20H95z' fill='%231976d2'/%3E%3C/svg%3E"
                        max-width="100" class="mx-auto"></v-img>
                      <p class="text-center text-grey mt-4 mb-0">
                        No supporting images added yet.<br />
                        <small>Click the + button to add supporting images</small>
                      </p>
                    </div>

                    <!-- Attachments Grid -->
                    <v-row v-else class="image-gallery">
                      <v-col v-for="(data, index) in AllProject" cols="12" sm="6" md="4" lg="3" :key="index">
                        <v-card class="image-card elevation-2" hover>
                          <div class="image-delete-action">
                            <v-btn icon x-small color="white" class="delete-btn" @click="deleteAttachmentRow(index)"
                              v-tooltip="'Delete Image'">
                              <v-icon>mdi-close-circle</v-icon>
                            </v-btn>
                          </div>
                          <v-img :src="data.AnswerEventImageUrl" aspect-ratio="1" class="image-preview"></v-img>
                          <v-card-text class="image-details pa-3">
                            <div class="detail-item mb-2">
                              <v-icon size="14" color="#ff9800" class="mr-1">mdi-calendar</v-icon>
                              <small class="detail-label">Date:</small>
                              <small class="detail-value">{{
                                data.AnswerEventDate
                                }}</small>
                            </div>
                            <div class="detail-item">
                              <v-icon size="14" color="#1976d2" class="mr-1">mdi-tag</v-icon>
                              <small class="detail-label">Event:</small>
                              <small class="detail-value text-truncate" :title="data.AnswerEventName">
                                {{ data.AnswerEventName }}
                              </small>
                            </div>
                          </v-card-text>
                        </v-card>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </template>

              <!-- Video Upload Section -->
              <v-card v-if="CriteriaAnswers.length > 0" class="video-upload-card elevation-3" style="margin-top: 1rem">
                <v-card-title class="video-upload-header">
                  <v-icon size="24" color="#d32f2f" class="mr-3">mdi-video</v-icon>
                  <span class="section-title">Video Submission</span>
                </v-card-title>

                <v-card-text class="pa-6">
                  <!-- Video Info Alert -->
                  <v-alert type="warning" text outlined class="video-info-alert mb-6">
                    <div class="d-flex align-center">
                      <v-icon size="20" color="#d32f2f" class="mr-3">mdi-alert-circle-outline</v-icon>
                      <div>
                        <strong>Video Requirements:</strong>
                        <div class="text-sm mt-1">
                          • Supported formats: MP4, AVI, MOV<br />
                          • Maximum file size: 5MB<br />
                          • This field is <strong>optional</strong>
                        </div>
                      </div>
                    </div>
                  </v-alert>

                  <!-- Video Upload Input -->
                  <v-row>
                    <v-col cols="12" md="8">
                      <div class="upload-wrapper">
                        <label class="upload-label">
                          <v-icon size="20" color="#d32f2f" class="mr-2">mdi-video-plus</v-icon>
                          Select Video File
                        </label>
                        <v-file-input ref="UploadVideo" v-model="UploadVideo" :rules="UploadVideoRules" counter
                          show-size truncate-length="50" :clearable="false" prepend-icon="mdi-folder-open"
                          accept="video/*" outlined dense class="video-input" @change="handleFileUpload($event)">
                          <template v-slot:append-outer>
                            <v-btn icon x-small color="error" @click="resetUploadVideo" v-if="UploadVideo.length > 0"
                              v-tooltip="'Clear Video'">
                              <v-icon>mdi-delete</v-icon>
                            </v-btn>
                          </template>
                        </v-file-input>
                      </div>
                    </v-col>
                    <v-col cols="12" md="4" class="d-flex align-center">
                      <div class="video-preview-info" v-if="UploadVideo.length > 0">
                        <v-chip color="#d32f2f" text-color="white" small class="video-chip">
                          <v-icon left size="16">mdi-check-circle</v-icon>
                          Video Selected
                        </v-chip>
                      </div>
                      <div class="video-preview-info" v-else>
                        <p class="text-grey text-center mb-0">
                          <small>No video uploaded</small>
                        </p>
                      </div>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
              <br />
              <v-row wrap v-if="ProgessStart == 1">
                <v-col align="center" cols="12" md="12">
                  <v-progress-circular :rotate="180" :size="100" :width="15" :value="uploadPercentage" color="#00a4ef">
                    {{ uploadPercentage }}
                  </v-progress-circular>
                  <h3 class="red--text">
                    Please wait, we are uploading your details. Time may vary
                    based on your internet connection.
                  </h3>
                </v-col>
              </v-row>
              <!-- Professional Submit Section -->
              <div class="submit-section" v-if="CriteriaAnswers.length > 0">
                <v-card class="submit-card" elevation="3">
                  <v-card-text class="text-center pa-6">
                    <div class="submit-info mb-4">
                      <h3 class="submit-title">Ready to Submit?</h3>
                      <p class="submit-description">
                        You can save your progress or submit your final
                        application.
                      </p>
                    </div>

                    <div class="action-buttons">
                      <v-btn @click.prevent="confirmAlert(4)" :loading="SaveFlag" :disabled="!valid1" elevation="3"
                        rounded large color="#4caf50" class="submit-btn white--text mr-4">
                        <v-icon left>mdi-content-save</v-icon>
                        Save Draft
                      </v-btn>

                      <v-btn :disabled="!valid1" @click.prevent="confirmAlert(3)" :loading="SubmitFlag" elevation="3"
                        rounded large color="#1976d2" class="submit-btn white--text">
                        <v-icon left>mdi-send</v-icon>
                        Final Submit
                      </v-btn>
                    </div>
                  </v-card-text>
                </v-card>
              </div>
            </div>
          </v-form>

          <v-row wrap v-if="ProgessStart == 1">
            <v-col align="center" cols="12" md="12">
              <v-progress-circular :rotate="180" :size="100" :width="15" :value="uploadPercentage" color="#00a4ef">{{
                uploadPercentage }}</v-progress-circular>
              <h3 class="red--text">
                Please wait, we are uploading your details. Time may vary based
                on your internet connection.
              </h3>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
    </v-card>

    <!-- Professional Dialogs -->
    <v-dialog v-model="addRecordPrompt" persistent max-width="800px" transition="dialog-transition"
      overlay-opacity="0.7">
      <answer-attachment-create pageTitle="Add Project Details" :recordData="selectedRecord"
        :addRecordPrompt="addRecordPrompt" @hideAddRecordPrompt="hideAddRecordPrompt($event)"
        v-if="addRecordPrompt"></answer-attachment-create>
    </v-dialog>

    <v-dialog v-model="addAttachmentRecordPrompt" persistent max-width="800px" transition="dialog-transition"
      overlay-opacity="0.7">
      <attachment-create pageTitle="Add Project Details" :addRecordPrompt="addAttachmentRecordPrompt"
        @hideAddRecordPrompt="hideAddAttachmentRecordPrompt($event)"
        v-if="addAttachmentRecordPrompt"></attachment-create>
    </v-dialog>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import Swal from "sweetalert2";

import AnswerAttachmentCreate from "@/view/pages/erp/jci-award/natcon/common/AnswerAttachmentCreate.vue";
import AttachmentCreate from "@/view/pages/erp/jci-award/natcon/common/AttachmentCreate.vue";
import { VueEditor } from "vue2-editor"; // Import Vue-Quill-Editor

export default {
  mixins: [common],
  components: {
    AnswerAttachmentCreate,
    AttachmentCreate,
    VueEditor,
  },
  data() {
    return {
      ProgessStart: 0,
      uploadPercentage: 0,

      OverlayLoadingFlag: false,

      valid1: true,
      valid2: true,
      LoadingFlag: false,
      SaveFlag: false,
      SubmitFlag: false,
      SearchFlag: false,
      CardLoadingFlag: false,
      UploadFlag: false,
      addButtonFlag: true,
      editorOptions: {
        modules: {
          toolbar: [
            ["bold", "italic", "underline", "strike"],
            ["blockquote"],
            [{ list: "ordered" }, { list: "bullet" }],
            [{ indent: "-1" }, { indent: "+1" }],
            [{ header: [1, 2, 3, 4, 5, 6, false] }],
            [{ color: [] }, { background: [] }],
            ["clean"],
          ],
        },
        placeholder: "Please provide your detailed answer here...",
      },

      Status: "",

      NatconLomAwardStartDate: "",
      NatconLomAwardEndDate: "",
      TotalNatconLomAward: 0,
      TotalApplied: 0,
      TotalPartiallySaved: 0,

      ValidateMemberProfileFlag: false,
      ValidateAwardCriteriaFlag: false,
      AutomaticScoreFlag: false,
      AttachmentFlag: false,

      StartDateFlag: false,
      EndDateFlag: false,

      rows: {},

      UploadVideo: [],
      UploadVideoRules: [
        (value) =>
          !value ||
          value.size < 25000000 ||
          "Upload Video size should be less than 25 MB!",
      ],

      AwardTypeTitleTxt: "",
      AwardTypeId: "",
      AwardCategoryId: "",
      NatconRegistrationCount: "",
      CriteriaAnswers: [],
      Project: [],
      selectedRecord: "",
      Id: "",
      showTextBox: false,

      addRecordPrompt: false,

      AllProjectMax: 10,
      AllProject: [],
      AllImages: [],
      addAttachmentRecordPrompt: false,

      ImageUploadFlag: false,

      AnswerRules: [(v) => !!v || "Answer is required"],
      NatconRegistrationFlag: false,

      PageInfo: {},
      PageTitle: "",
      PageDescription: "",
      CurrentYearId: 0,
      YearName: "",
      NextYearCode: "",
      NextYearName: "",
    };
  },
  computed: {},
  watch: {
    PageInfo: function () {
      console.log("watch PageInfo");
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        var zone_name = this.$session.get("ZoneName");
        zone_name = zone_name == (null || undefined) ? 0 : zone_name;

        var flag = tr.PagePermissionFlag;
        console.log("PagePermissionFlag=" + flag);

        if (!flag) {
          this.$router.push("/access-denied");
        }
        this.PageTitle = tr.PageTitle + " - " + zone_name;
        this.PageDescription = tr.PageDescription;

        var CurrentJciYearId = parseInt(tr.CurrentJciYearId);
        console.log({ CurrentJciYearId });

        var YearName = parseInt(tr.CurrentJciYearName);
        console.log({ YearName });
        this.YearName = YearName;

        this.CurrentYearId = CurrentJciYearId;

        this.pageData();
      }
    },
  },
  mounted() { },
  methods: {
    // HTML Editor related methods
    getCharacterCount(html) {
      if (!html) return 0;
      // Remove HTML tags and entities to count only visible text
      const text = html.replace(
        /<[^>]*>|&nbsp;|&amp;|&lt;|&gt;|&quot;|&#39;/g,
        ""
      );
      return text.length;
    },

    onTextChange(event, item) {
      // Get the Quill editor instance from the event
      const editor = event.editor;
      const maxLength = item.CriteriaQuestionsCharsCount;

      // Calculate the current text length without HTML tags
      const currentLength = this.getCharacterCount(item.AnswerText);

      // If the length exceeds the limit, prevent further input
      if (currentLength > maxLength) {
        // Get the current selection
        const range = editor.getSelection();

        // If there is no selection, we need to restore the previous text
        if (range && range.length === 0) {
          // Store current cursor position
          const cursorPosition = range.index;

          // Use DOM manipulation to truncate the HTML content
          const editorRoot = editor.root;
          const editorContent = editorRoot.innerHTML;

          // Create a temporary div to manipulate the HTML
          const tempDiv = document.createElement("div");
          tempDiv.innerHTML = editorContent;

          // Function to recursively process text nodes
          const processNode = (node, currentCount) => {
            if (currentCount >= maxLength) {
              // Remove node completely if we're already at the limit
              if (node.parentNode) {
                node.parentNode.removeChild(node);
              }
              return currentCount;
            }

            if (node.nodeType === Node.TEXT_NODE) {
              const nodeLength = node.textContent.length;
              if (currentCount + nodeLength > maxLength) {
                // Truncate this text node
                const remainingChars = maxLength - currentCount;
                node.textContent = node.textContent.substring(
                  0,
                  remainingChars
                );
                return maxLength;
              } else {
                return currentCount + nodeLength;
              }
            } else if (node.nodeType === Node.ELEMENT_NODE) {
              // Skip certain elements that don't contribute to visible text
              if (node.tagName === "SCRIPT" || node.tagName === "STYLE") {
                return currentCount;
              }

              // Process child nodes
              let count = currentCount;
              const childNodes = Array.from(node.childNodes);
              for (const childNode of childNodes) {
                count = processNode(childNode, count);
                if (count >= maxLength) break;
              }
              return count;
            }

            return currentCount;
          };

          // Process the content
          processNode(tempDiv, 0);

          // Update the editor content
          editor.setContents(editor.clipboard.convert(tempDiv.innerHTML));

          // Restore cursor position, but don't go beyond the new content
          const newLength = editor.getText().length;
          const newPosition = Math.min(cursorPosition, newLength - 1);
          editor.setSelection(newPosition > 0 ? newPosition : 0);
        }
      }

      // Update validation rules
      if (currentLength >= maxLength) {
        if (!this.AnswerRules.includes(this.validateMaxLength)) {
          this.AnswerRules.push(this.validateMaxLength);
        }
      } else {
        const index = this.AnswerRules.indexOf(this.validateMaxLength);
        if (index !== -1) {
          this.AnswerRules.splice(index, 1);
        }
      }
    },

    validateMaxLength(v) {
      // This is a custom validation rule for the rich text editor
      // It will be used by Vuetify's validation system
      const item = this.CriteriaAnswers.find((item) => item.AnswerText === v);
      if (
        item &&
        this.getCharacterCount(v) > item.CriteriaQuestionsCharsCount
      ) {
        return "Character limit exceeded";
      }
      return true;
    },
    refreshPageData() {
      console.log("refreshPageData called");
      this.LoadingFlag = true;
      var url1 = "api/menu-details/show";
      var condition1 = {
        UserInterface: 1,
        Module: "jac_national_zone_award",
        Action: "create",
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
    },
    pageData() {
      console.log("pageData called");

      var id = this.$route.query.id;
      console.log({ id });
      this.AwardTypeId = id;

      this.getAwardTypeDetials();
      this.getCriteriaAnswers();
    },
    resetUploadVideo() {
      console.log("resetUploadVideo called.");
      this.UploadVideo = "";
    },
    resetForm() {
      console.log("resetForm called");
      var rows = this.CriteriaAnswers;

      rows.map((row, index) => {
        row.AnswerText = "";
        row.AllProject = {};
      });

      this.getAwardTypeDetials();
      this.getCriteriaAnswers();
    },
    addNewRow(tr) {
      console.log("addNewRow called");
      console.log({ tr });
      this.selectedRecord = tr;
      this.addRecordPrompt = true;
    },
    deleteRow(id, index) {
      console.log("deleteRow called");
      console.log({ id });
      console.log({ index });
      if (index > -1) {
        var rows = this.CriteriaAnswers;

        rows.map((row, k) => {
          console.log({ k });
          if (id == k) {
            row.AllProject.splice(index, 1);

            // this.AllProject.splice(index, 1);
            // this.ProjectDetails = "";
          }
        });

        console.log("CriteriaAnswers = ", this.CriteriaAnswers);
        this.CriteriaAnswers = this.CriteriaAnswers;
      }
    },
    hideAddRecordPrompt(tr) {
      console.log("hideAddRecordPrompt called");
      this.addRecordPrompt = false;
      console.log({ tr });

      if (tr.EventId != "") {
        this.Id = tr.Index;
        var rows = this.CriteriaAnswers;

        // iterate foreach for CriteriaAnswers
        rows.map((row, index) => {
          console.log({ index });

          if (tr.Index == index) {
            var n2 = row.AllProject.length;
            console.log({ n2 });

            var n3 = row.CriteriaQuestionsAttachmentMax;
            console.log({ n3 });

            // For first Attachment
            if (n2 == 1) {
              // iterate foreach for AllProject

              row.AllProject.map((data, k) => {
                console.log({ k });
                var eventid = data.AnswerEventId;
                console.log({ eventid });
                if (eventid == "") {
                  row.AllProject.splice(k, 1);
                }

                var m1 = row.AllProject.length;
                console.log({ m1 });

                if (m1 < n3) {
                  row.AllProject.push({
                    AnswerEventId: tr.EventId,
                    AnswerEventName: tr.EventName,
                    AnswerEventDate: tr.EventDate,
                    AnswerPortfolioName: tr.PortfolioName,
                    AnswerEventPhoto: tr.EventPhoto,
                    AnswerEventImages: tr.EventImages,
                    AnswerInvitationImages: tr.InvitationImages,
                    AnswerJciEventDescription: tr.EventDescription,
                  });
                } else {
                  console.log("Maximum Limit Crossed");
                  var Message = "Attachment Maximum Count reached its limit.";
                  this.sweetAlert("error", Message, false);
                }
              });

              console.log("CriteriaAnswers = ", this.CriteriaAnswers);
              this.CriteriaAnswers = this.CriteriaAnswers;
            } else {
              if (n2 < n3) {
                // For after first Attachment
                row.AllProject.push({
                  AnswerEventId: tr.EventId,
                  AnswerEventName: tr.EventName,
                  AnswerEventDate: tr.EventDate,
                  AnswerPortfolioName: tr.PortfolioName,
                  AnswerEventPhoto: tr.EventPhoto,
                  AnswerEventImages: tr.EventImages,
                  AnswerInvitationImages: tr.InvitationImages,
                  AnswerJciEventDescription: tr.EventDescription,
                });
              } else {
                console.log("Maximum Limit Crossed");
                var Message = "Attachment Maximum Count reached its limit.";
                this.sweetAlert("error", Message, false);
              }
            }
          }
        });

        console.log("CriteriaAnswers = ", this.CriteriaAnswers);
        this.CriteriaAnswers = this.CriteriaAnswers;
      }
    },
    addAttachmentNewRow() {
      console.log("addAttachmentNewRow called");
      this.addAttachmentRecordPrompt = true;
    },
    deleteAttachmentRow(id) {
      console.log("deleteAttachmentRow called");
      console.log({ id });

      this.AllProject.splice(id, 1);
      this.AllImages.splice(id, 1);
    },
    hideAddAttachmentRecordPrompt(tr) {
      console.log("hideAddAttachmentRecordPrompt called");
      this.addAttachmentRecordPrompt = false;
      console.log({ tr });

      if (tr.EventName != "") {
        var count = this.AllProject.length;
        var id = count == 0 ? 0 : count + 1;

        this.AllProject.push({
          AnswerEventId: "",
          AnswerEventName: tr.EventName,
          AnswerEventDate: tr.EventDate,
          AnswerEventDescription: tr.EventDescription,
          AnswerEventImageName: tr.EventImageName,
          AnswerEventImageUrl: tr.EventImageUrl,
          AnswerEventImage: tr.EventImage,
        });

        this.AllImages.push({
          AnswerEventId: "",
          AnswerEventName: tr.EventName,
          UploadedImage: tr.EventImage.UploadedImage,
        });
      }
      console.log("AllProject = " + JSON.stringify(this.AllProject));
    },
    getAwardTypeDetials() {
      console.log("getAwardTypeDetials called");

      var validate1 = true;
      console.log({ validate1 });

      if (validate1) {
        this.AwardCategoryId = "";
        this.CardLoadingFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/award-type/show";

        var upload = {
          UserInterface: 1,
          AwardType: this.AwardTypeId,
          AwardGroup: 2,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
          server_url +
          ", token=" +
          token +
          ", add_url=" +
          add_url
        );

        const thisIns = this;
        var output = "";
        var records = "";
        var flag = 0;
        this.NatconRegistrationCount = "";
        this.NatconRegistrationFlag = false;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            thisIns.CardLoadingFlag = false;

            flag = response.data.flag;
            output = response.data.output;
            console.log("output=" + output + ", flag=" + flag);

            records = response.data.records;
            console.log({ records });

            if (flag == 1) {
              console.log("records = " + JSON.stringify(records));
              thisIns.AwardTypeTitleTxt = records.AwardTypeTitleTxt;
              thisIns.AwardCategoryId = records.AwardCategoryId;
              thisIns.AwardCategoryId = records.AwardCategoryId;
              thisIns.NatconRegistrationFlag = records.NatconRegistrationFlag;
              // thisIns.NatconRegistrationFlag = false;
              thisIns.NatconRegistrationCount = records.NatconRegistrationCount;
            } else {
              thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            thisIns.CardLoadingFlag = false;
            console.log("error=" + error);
            thisIns.toast("error", error);
          });
      } else {
        var message = "";
        console.log("error=" + message);
      }
    },
    getCriteriaAnswers() {
      console.log("getCriteriaAnswers called");

      var AwardType = this.AwardTypeId;
      console.log({ AwardType });

      if (AwardType > 0) {
        this.tableData1 = [];
        this.selected1 = [];

        this.LoadingFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/judging-criteria/questions-lists";
        var upload = {
          UserInterface: 1,
          AwardType: AwardType,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
          server_url +
          ", token=" +
          token +
          ", add_url=" +
          add_url
        );

        const thisIns = this;
        var output = "";
        var records = [];
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            thisIns.LoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            console.log("output=" + output + ", flag=" + flag);

            records = response.data.records;
            console.log({ records });

            if (flag == 1) {
              thisIns.CriteriaAnswers = records;
              // thisIns.toast("success", output);
            } else {
              console.log("error=" + output);
              // thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.LoadingFlag = false;
          });
      } else {
        var message = "Kindy select the Award Type";
        this.toast("error", message);
      }
    },
    getTotalNatConLomAwardsDetails(status) {
      console.log("getTotalNatConLomAwardsDetails called");

      var validate1 = true;
      console.log({ validate1 });

      console.log({ status });
      this.Status = status;

      if (validate1) {
        this.CardLoadingFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/award-type/total-awards";

        var AwardCategory = 4;
        var AwardGroup = 2;

        if (this.RoleId == 2) {
          // For Admin
          var upload = {
            UserInterface: 1,
            AwardCategory: AwardCategory,
            AwardGroup: AwardGroup,
            Lom: this.LomCode,
          };
        } else {
          // For Others
          var upload = {
            UserInterface: 1,
            AwardCategory: AwardCategory,
            AwardGroup: AwardGroup,
          };
        }

        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
          server_url +
          ", token=" +
          token +
          ", add_url=" +
          add_url
        );

        const thisIns = this;
        var output = "";
        var records = "";
        var successTxt = "";
        var errorTxt = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            thisIns.CardLoadingFlag = false;

            flag = response.data.flag;
            output = response.data.output;
            records = response.data.records;
            console.log("output=" + output + ", flag=" + flag);
            successTxt = response.data.success;
            errorTxt = response.data.error;
            console.log("successTxt=" + successTxt + ", errorTxt=" + errorTxt);

            if (flag == 1) {
              console.log("records = " + JSON.stringify(records));
              thisIns.rows = records;
              thisIns.NatconLomAwardStartDate = records.StartDateTxt;
              thisIns.NatconLomAwardEndDate = records.EndDateTxt;
              thisIns.TotalNatconLomAward = records.TotalAward;
              thisIns.TotalApplied = records.TotalApplied;
              thisIns.TotalPartiallySaved = records.TotalPartiallySaved;
              thisIns.StartDateFlag = records.StartDateFlag;
              thisIns.EndDateFlag = records.EndDateFlag;

              console.log(
                "StartDateFlag=" +
                records.StartDateFlag +
                ", EndDateFlag=" +
                records.EndDateFlag
              );

              if (thisIns.StartDateFlag && thisIns.EndDateFlag) {
                thisIns.confirmAlert(status);
              } else {
                thisIns.deadlineAlert();
              }
            } else {
              thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            thisIns.CardLoadingFlag = false;
            console.log("error=" + error);
            thisIns.toast("error", error);
          });
      } else {
        var message = "";
        console.log("error=" + message);
      }
    },
    deadlineAlert() {
      console.log("deadlineAlert called");
      var htmlTxt = "";
      htmlTxt +=
        "<h3> Natcon Lom Awards Bidding ended at <b> " +
        this.NatconLomAwardEndDate +
        " </b> </h3>";
      // htmlTxt += "<br/> Start <b> " + this.NatconLomAwardEndDate + " </b>";

      Swal.fire({
        title: "Sorry !",
        text: "",
        icon: "warning",
        html: htmlTxt,
      });
    },
    confirmAlert(status) {
      console.log("confirmAlert called");
      var video_validate = false;
      var validate1 = false;

      video_validate =
        this.UploadVideo == "" ? true : this.$refs.UploadVideo.validate();
      validate1 = status == 4 ? true : this.$refs.form1.validate();
      console.log({ status }, { video_validate }, { validate1 });

      if (validate1 && video_validate) {
        Swal.fire({
          title: "Are you sure?",
          text: "You won't be able to revert this!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: `Save`,
          confirmButtonColor: "#3085d6",
          cancelButtonText: `Cancel`,
          cancelButtonColor: "#d33",
        }).then((result) => {
          if (result.isConfirmed) {
            // Delete Empty event id in AllProject
            var rows = this.CriteriaAnswers;

            rows.map((row, index) => {
              console.log({ index });
              var n2 = row.AllProject.length;
              console.log({ n2 });
              if (n2 == 1) {
                // iterate foreach for AllProject

                row.AllProject.map((data, k) => {
                  console.log({ k });
                  var eventid = data.AnswerEventId;
                  console.log({ eventid });
                  if (eventid == "") {
                    row.AllProject.splice(k, 1);
                  }
                });
              }
            });

            console.log("CriteriaAnswers = ", this.CriteriaAnswers);
            this.CriteriaAnswers = this.CriteriaAnswers;

            this.submitForm(status);
          } else {
            this.sweetAlert("error", "You cancelled the process", false);
          }
        });
      } else {
        var message = "";
        if (!validate1) {
          message += "kindly answer all the questions.";
        }
        if (!video_validate) {
          message +=
            "The video should be <br/> 1. In MP4 or AVI or MOV file format <br/> 2. Must be less than 25MB in file size";
        }

        Swal.fire({
          // title: "Are you sure?",
          icon: "error",
          html: message,
        });
        // this.sweetAlert("error", message, false);
      }
    },
    submitForm(status) {
      console.log("submitForm is called");

      this.ProgessStart = 1;
      this.OverlayLoadingFlag = true;

      if (status == 4) {
        this.SaveFlag = true;
      } else {
        this.SubmitFlag = true;
      }

      console.log({ status });
      var count = this.AllImages.length;

      var video = this.UploadVideo != {} ? this.UploadVideo : {};
      console.log({ video });

      this.AllImages.forEach((row, index) => {
        console.log({ index });
        row.AnswerEventId = index;
        console.log("AllImages=" + JSON.stringify(this.AllImages));
      });

      this.AllProject.forEach((item, index) => {
        console.log({ index });
        item.AnswerEventId = index;
        console.log("AllProject=" + JSON.stringify(this.AllProject));
      });

      var inputData = {
        ActiveStatus: status,
        AwardType: this.AwardTypeId,
        CriteriaAnswers: this.CriteriaAnswers,
        AllProject: this.AllProject,
        AllImagesCount: count,
      };
      console.log("inputData=" + JSON.stringify(inputData));

      let upload = new FormData();
      upload.append("UserInterface", 1);
      upload.append("InputData", JSON.stringify(inputData));
      upload.append("UploadVideo", video);

      this.AllImages.forEach((row, index) => {
        var id = index + 1;
        console.log({ id });
        var UploadedImage = row["UploadedImage"];
        var EventId = row["AnswerEventId"];
        console.log({ UploadedImage }, { EventId });

        upload.append("AllImages" + index, UploadedImage, EventId);
      });

      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;

      var add_url = server_url + "api/award/national-award-store";
      console.log(
        "server_url=" + server_url + ", token=" + token + ", add_url=" + add_url
      );

      const thisIns = this;
      var output = "";
      var records = "";
      var flag = 0;

      this.$http({
        url: add_url,
        method: "POST",
        data: upload,
        headers: {
          // "Content-Type": "application/json",
          "Content-Type": "multipart/form-data",
          Authorization: "Bearer " + token,
        },
        onUploadProgress: function (progressEvent) {
          this.uploadPercentage = parseInt(
            Math.round((progressEvent.loaded / progressEvent.total) * 100)
          );
        }.bind(this),
      })
        .then(function (response) {
          console.log("response=" + JSON.stringify(response));

          thisIns.ProgessStart = 0;
          thisIns.OverlayLoadingFlag = false;

          if (status == 4) {
            thisIns.SaveFlag = false;
          } else {
            thisIns.SubmitFlag = false;
          }

          output = response.data.output;
          flag = response.data.flag;
          console.log("output=" + output + ", flag=" + flag);
          records = response.data.records;
          console.log({ records });

          if (flag == 1) {
            thisIns.sweetAlert("success", output, true);
            thisIns.$router.push("/jac/national/zone-award/list");
          } else {
            thisIns.sweetAlert("error", output, false);
          }
        })
        .catch(function (error) {
          console.log("error=" + error);
          thisIns.ProgessStart = 0;
          thisIns.OverlayLoadingFlag = false;
          if (status == 4) {
            thisIns.SaveFlag = false;
          } else {
            thisIns.SubmitFlag = false;
          }
        });
    },
  },

  beforeMount() {
    this.refreshPageData();
  },
};
</script>
<style lang="scss">
// Professional Award Form Styles (adapted from ZoneCon UI)
#natcon-lom-award-create {
  background-color: #f5f7fa;
  min-height: 100vh;

  .professional-overlay {
    .loading-container {
      text-align: center;
      color: white;

      .loading-text {
        font-size: 1.1rem;
        font-weight: 500;
        margin: 0;
      }
    }
  }

  .main-form-container {
    border-radius: 12px !important;
    overflow: hidden;
    background: white;
    box-shadow: 0 6px 30px rgba(0, 0, 0, 0.1) !important;
  }

  .professional-form-header {
    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
    border-bottom: 1px solid #e3f2fd;

    .header-content {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      width: 100%;
    }

    .action-buttons {
      display: flex;
      gap: 0.5rem;
    }

    .form-main-title {
      font-size: 1.75rem;
      font-weight: 700;
      color: #2c3e50;
      margin: 0;
    }

    .award-badge {
      margin-top: 0.5rem;
    }
  }

  .description-section {
    .description-wrapper {
      display: flex;
      align-items: center;
      background: rgba(25, 118, 210, 0.05);
      padding: 1rem;
      border-radius: 8px;
      border-left: 4px solid #1976d2;

      .form-description {
        font-size: 1.05rem;
        color: #424242;
        margin: 0;
      }
    }
  }

  .form-main-content {
    background: #f5f7fa;
  }

  .form-loading-section {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;

    .loading-wrapper {
      text-align: center;

      .loading-text {
        font-size: 1.1rem;
        color: #666;
        font-weight: 500;
        margin: 0;
      }
    }
  }

  .criteria-card,
  .submit-card,
  .empty-state-card {
    border-radius: 12px !important;
    border: 1px solid #e0e0e0;
  }

  .criteria-header {
    background: linear-gradient(135deg, #fff 0%, #f8f9fa 100%);
    border-bottom: 1px solid #e0e0e0;
    padding: 1.5rem;

    .section-title {
      font-size: 1.3rem;
      font-weight: 600;
      color: #2c3e50;
    }
  }

  .criteria-detail-card {
    display: flex;
    align-items: center;
    background: #fff3e0;
    border: 1px solid #ffb74d;
    border-radius: 8px;
    padding: 1rem;

    .criteria-title {
      font-size: 1.2rem;
      font-weight: 600;
      color: #e65100;
      margin: 0;
    }
  }

  .question-content-wrapper {
    margin-bottom: 1.5rem;
  }

  .question-label-section {
    .question-title {
      font-size: 1rem;
      font-weight: 600;
      color: #263238;
      margin: 0;
      display: flex;
      align-items: center;
      gap: 0.25rem;
    }
  }

  .html-editor-wrapper {
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    overflow: hidden;
  }

  .professional-editor .ql-container {
    min-height: 140px;
  }

  .character-counter {
    padding: 6px 10px;
    font-size: 0.85rem;
    color: #607d8b;
    text-align: right;
    background: #fafafa;
    border-bottom-left-radius: 6px;
    border-bottom-right-radius: 6px;
  }

  .character-counter.error--text {
    color: #d32f2f;
    background: rgba(244, 67, 54, 0.06);
    font-weight: 600;
  }

  .professional-textarea .v-input__slot {
    min-height: 120px;
  }

  .attachments-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .attachments-grid .attachment-card {
    border-radius: 8px !important;
    position: relative;
    transition: transform 0.2s, box-shadow 0.2s;
  }

  .attachment-actions {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    z-index: 2;
  }

  .event-image {
    border-radius: 4px;
    border: 1px solid #e0e0e0;
    object-fit: cover;
    transition: transform 0.2s;
  }

  .event-image:hover {
    transform: scale(1.05);
  }

  .character-counter {
    padding: 4px 12px;
    font-size: 0.75rem;
    color: rgba(0, 0, 0, 0.6);
    background: #fafafa;
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
  }

  .natcon-alert-card {
    border-left: 4px solid #ff0505;
    background: linear-gradient(90deg,
        rgba(25, 118, 210, 0.03),
        rgba(255, 255, 255, 0.01));
    border-radius: 8px;
    transition: box-shadow 0.18s ease, transform 0.18s ease;
  }

  .natcon-alert-title {
    font-weight: 700;
    font-size: 1.05rem;
    color: #ff0505;
  }

  .natcon-alert-msg {
    color: #ff0505;
    margin-top: 6px;
    font-size: 0.95rem;
    line-height: 1.35;
  }

  .natcon-alert-extra {
    color: rgba(198, 40, 40, 0.95);
    font-size: 0.92rem;
    line-height: 1.3;
  }

  .natcon-success-card {
    border-left: 4px solid #2e7d32;
    background: linear-gradient(90deg,
        rgba(46, 125, 50, 0.04),
        rgba(255, 255, 255, 0.01));
    border-radius: 8px;
    transition: box-shadow 0.18s ease, transform 0.18s ease;
  }

  .submit-section .submit-card {
    border: 2px solid #e3f2fd;
    background: linear-gradient(135deg, #fff 0%, #f8f9fa 100%);
  }

  .submit-title {
    font-size: 1.4rem;
    font-weight: 600;
    color: #2c3e50;
    margin: 0;
  }

  .submit-description {
    color: #666;
    margin: 0.5rem 0 0 0;
  }

  .empty-state-card .empty-state-title {
    font-size: 1.3rem;
    font-weight: 600;
    color: #666;
    margin: 0;
  }

  .empty-state-text {
    color: #999;
    margin: 0.5rem 0 0 0;
  }

  /* Additional Attachments Section */
  .attachments-additional-card {
    border-radius: 12px !important;
    border: 1px solid #e0e0e0;
    background: white;
  }

  .attachments-additional-header {
    background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
    border-bottom: 1px solid #ffb74d;
    padding: 1.5rem;

    .section-title {
      font-size: 1.3rem;
      font-weight: 600;
      color: #e65100;
    }
  }

  .add-attachment-action-btn {
    transition: all 0.2s ease;

    &:hover {
      transform: scale(1.15);
      background: rgba(25, 118, 210, 0.1) !important;
    }
  }

  .info-alert {
    background: rgba(25, 118, 210, 0.04) !important;
    border: 1px solid rgba(25, 118, 210, 0.2) !important;
    border-radius: 8px;

    .text-sm {
      font-size: 0.9rem;
      line-height: 1.4;
      color: #424242;
    }
  }

  .empty-state-container {
    text-align: center;
    padding: 2rem 1rem;
    border: 2px dashed #ccc;
    border-radius: 8px;
    background: #f5f5f5;
  }

  .image-gallery {
    .image-card {
      border-radius: 8px !important;
      position: relative;
      overflow: hidden;
      transition: all 0.3s ease;

      &:hover {
        transform: translateY(-4px);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15) !important;
      }

      .image-delete-action {
        position: absolute;
        top: 0;
        right: 0;
        z-index: 10;
        background: rgba(211, 47, 47, 0.9);
        border-radius: 0 0 0 8px;
        padding: 2px;

        .delete-btn {
          transition: all 0.2s ease;

          &:hover {
            background: rgba(211, 47, 47, 1) !important;
          }
        }
      }

      .image-preview {
        border-bottom: 1px solid #e0e0e0;
      }

      .image-details {
        background: #fafafa;

        .detail-item {
          display: flex;
          align-items: center;
          gap: 4px;

          .detail-label {
            font-weight: 600;
            color: #424242;
            min-width: 40px;
          }

          .detail-value {
            color: #666;
            flex: 1;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }
      }
    }
  }

  /* Video Upload Section */
  .video-upload-card {
    border-radius: 12px !important;
    border: 1px solid #e0e0e0;
    background: white;
  }

  .video-upload-header {
    background: linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%);
    border-bottom: 1px solid #ef5350;
    padding: 1.5rem;

    .section-title {
      font-size: 1.3rem;
      font-weight: 600;
      color: #c62828;
    }
  }

  .video-info-alert {
    background: rgba(211, 47, 47, 0.04) !important;
    border: 1px solid rgba(211, 47, 47, 0.2) !important;
    border-radius: 8px;

    .text-sm {
      font-size: 0.9rem;
      line-height: 1.4;
      color: #424242;
    }
  }

  .upload-wrapper {
    .upload-label {
      display: flex;
      align-items: center;
      font-weight: 600;
      color: #424242;
      margin-bottom: 12px;
      font-size: 0.95rem;
    }

    .video-input {
      &:deep(.v-input__slot) {
        min-height: 56px;
        background: #f5f5f5;
        border-radius: 8px;

        input {
          color: #424242;
        }
      }
    }
  }

  .video-preview-info {
    .video-chip {
      display: flex;
      gap: 6px;
      font-weight: 500;
    }
  }

  .pulse-icon {
    animation: pulse 1.4s infinite ease-in-out;
  }

  @keyframes pulse {
    0% {
      transform: scale(1);
      opacity: 1;
    }

    50% {
      transform: scale(1.12);
      opacity: 0.85;
    }

    100% {
      transform: scale(1);
      opacity: 1;
    }
  }

  @media (max-width: 1024px) {
    .professional-form-header .header-content {
      flex-direction: column;
      gap: 1rem;

      .action-buttons {
        align-self: flex-start;
      }
    }
  }

  @media (max-width: 768px) {
    .main-form-container {
      margin: 0.5rem;
      border-radius: 8px !important;
    }

    .professional-form-header {
      padding: 1rem !important;

      .form-main-title {
        font-size: 1.5rem;
      }
    }

    .action-buttons {
      flex-direction: column;

      .submit-btn {
        width: 100%;
        margin: 0.25rem 0 !important;
      }
    }
  }
}
</style>